import { buildYetiScoresForKpis, yetiBucketForWindow, yetiInputsForWindow } from "../../hooks/company";
import { rawTargetOf } from "../../helpers/kpiTargetScale";
import { isBidAccuracyKpi } from "../../helpers/scoreAggregate";

/**
 * The Yeti's pace for ONE Build Your Own tile, and where the tile's value
 * stands against it.
 *
 * The pace is read straight off the per-workspace Yeti that useScopedMetrics
 * already builds for the Beat the Yeti card (`yetiByWs`) — never recomputed
 * here, so this tile and that card can not disagree about the same metric.
 *
 * A metric shared by name across several workspaces has a different KPI id in
 * each, so the pace is read per workspace (through the tile's own
 * `_perWorkspace` list) and averaged over the workspaces that have one. The
 * tile's value is that same per-rep average, so the two sides are like for like.
 */

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const normWin = (w) => (w === "year" ? "ytd" : w);

/* A rate has no "total": Team total on a % metric is the same blended rate
   Average shows, so it is compared with the Yeti's rate unscaled. */
export const isPercentLike = (def) => !!def && (def.kind === "%" || isBidAccuracyKpi(def));

/* Reps a Team total is the sum of, for this tile (0 = not a scalable total). */
export const teamSizeOf = (metric) => {
  if (!metric || isPercentLike(metric)) return 0;
  return (Array.isArray(metric._perWorkspace) ? metric._perWorkspace : [])
    .reduce((n, w) => n + (Number(w.scorers) || 0), 0);
};

/* Rates and averages do not grow with the number of days elapsed: their pace IS
   their target, whatever the window (BeatTheYeti/pace.js states the same
   rule). The Yeti builder only honours that for anchor / whole-month metrics;
   a rate configured any other way would come out as target x business days, so
   pin it here rather than print a 4,400% Closed Rate. */
const periodIndependent = (def) => !!def && (def.kind === "%" || def.calc === "avg");
const paceAmt = (def, cell) => {
  const anchored = def?.wholeMonth || def?.metricType === "anchor";
  if (periodIndependent(def) && !anchored) {
    const t = num(def.minimum) || num(def.target);
    if (t > 0) return t;
  }
  /* Everything else is the builder's own arithmetic, the same one the Beat the
     Yeti card runs: a monthly "sum" target is spread over business days, a
     daily (progressive) target is multiplied by the days elapsed, and an
     anchor / whole-month target stands as it is. */
  return num(cell.amt);
};

/**
 * @param {object} metric  the tile's KPI (may carry `_perWorkspace`, `_seriesIds`)
 * @param {object} opts
 * @param {object} opts.yetiByWs  { [workspaceId]: buildYetiScoresForKpis(...) }
 * @param {string} opts.yetiWin   the window yetiByWs was built for
 * @param {string} opts.window    the window this tile is drawing
 * @param {string[]} opts.workspaceIds  the workspaces the card reads
 * @param {string} opts.show      "avg" | "total"
 * @returns {{amt:number, pts:number, workspaces:number}|null}
 *   null = there is no honest Yeti to compare with, and nothing is drawn.
 */
export const yetiPaceFor = (metric, {
  yetiByWs, yetiWin, window: win, workspaceIds = [], show = "avg",
} = {}) => {
  if (!metric || metric._derived || !yetiByWs) return null;
  /* Team total is a sum over every rep; the Yeti is ONE rep's pace. Setting
     them side by side would call a team "ahead" for having more people, so the
     pace is scaled up to the team it is compared with: each workspace's pace
     x the reps who scored the metric there, summed across workspaces. A rate
     is not summed (see isPercentLike) and compares as it is. */
  const team = show === "total" && !isPercentLike(metric);
  if (team && !teamSizeOf(metric)) return null;
  /* A metric-plan card widens its fetch window past its own, so the Yeti may
     have been built for a different window than the one on screen. */
  if (!yetiWin || normWin(yetiWin) !== normWin(win || "month")) return null;

  const bucket = yetiBucketForWindow(normWin(yetiWin));
  const cells = [];
  const perWs = Array.isArray(metric._perWorkspace) ? metric._perWorkspace : [];
  if (perWs.length) {
    perWs.forEach(({ workspaceId, def, scorers }) => {
      const cell = yetiByWs[String(workspaceId)]?.[bucket]?.[String(def?._id)];
      const k = team ? num(scorers) : 1;
      const amt = cell ? paceAmt(def, cell) * k : 0;
      if (amt > 0) cells.push({ amt, pts: num(cell.pts) * k });
    });
  } else if (!team && workspaceIds.length === 1) {
    const ids = [metric._id, ...(metric._seriesIds || []), ...(metric._sourceIds || [])].filter(Boolean).map(String);
    for (let i = 0; i < ids.length; i += 1) {
      const cell = yetiByWs[String(workspaceIds[0])]?.[bucket]?.[ids[i]];
      const amt = cell ? paceAmt(metric, cell) : 0;
      if (amt > 0) {
        cells.push({ amt, pts: num(cell.pts) });
        break;
      }
    }
  }
  if (!cells.length) return null;
  const fold = (pick) => {
    const sum = cells.reduce((a, c) => a + num(c[pick]), 0);
    return team ? sum : sum / cells.length;
  };
  return { amt: fold("amt"), pts: fold("pts"), workspaces: cells.length, team: team ? teamSizeOf(metric) : 0 };
};

/**
 * The goal a real anchor metric (the "twin", see helpers/derivedGoal) lends a
 * derived tile, with the Yeti's pace on it.
 *
 * The pace comes out of the SAME builder the Beat the Yeti card runs
 * (buildYetiScoresForKpis, over the same window inputs) and is read back through
 * yetiPaceFor exactly as a real tile's is — so a derived tile and its twin can
 * not disagree about what the Yeti has done by now.
 *
 * @param {object} twin  the anchor KPI def
 * @param {string} win   resolved window id ("year" already folded into "ytd")
 * @param {moment.Moment} from  window start
 * @param {moment.Moment} to    window end
 * @param {Array} holidays  the company calendar's holidays
 */
const TWIN_WS = "derived-twin";
export const twinGoalFor = (twin, win, from, to, holidays) => {
  if (!twin) return null;
  const goal = { target: rawTargetOf(twin), title: twin.title, metricType: twin.metricType, yeti: null };
  if (!from?.isValid?.() || !to?.isValid?.()) return goal;
  const { monthRef, asOf, range } = yetiInputsForWindow(win, from, to);
  const built = buildYetiScoresForKpis([twin], monthRef, holidays, asOf, range);
  goal.yeti = yetiPaceFor({ ...twin, kind: "$" }, {
    yetiByWs: { [TWIN_WS]: built },
    yetiWin: win,
    window: win,
    workspaceIds: [TWIN_WS],
    show: "avg",
  });
  return goal;
};

/** Lower-is-better metrics (direction -1) flip which side of the Yeti wins. */
export const lowerIsBetter = (metric) => (metric?.direction ?? 1) < 0;

/**
 * @param {number} value   the tile's amount
 * @param {number} yetiAmt the Yeti's pace amount (> 0)
 * @param {object} metric
 * @param {(n:number)=>string} format  prints an amount the way the tile does
 * @returns {{ level:boolean, ahead:boolean, diffText:string }}
 *   `level` = the two print identically, so no arrow / colour is claimed.
 */
export const yetiStanding = (value, yetiAmt, metric, format) => {
  const diff = num(value) - num(yetiAmt);
  const diffText = format(Math.abs(diff));
  /* Judged on what is PRINTED: a gap that rounds to "$0" or "0%" must not
     wear a red arrow. */
  const level = /^[^0-9]*0(\.0+)?[^0-9]*$/.test(diffText);
  const ahead = lowerIsBetter(metric) ? diff <= 0 : diff >= 0;
  return { level, ahead, diffText };
};
